import moment from 'moment'
import {
    QUOTE_CREATE,
    QUOTE_SEARCH,
    QUOTE_ASSIGN_REQUESTER,
    QUOTE_AGGREGATED_DATA,
    QUOTE_GET,
    QUOTE_UPDATE_REASON,
} from '@/data/url'
import apiClient from '@/services/apiClient'
import { getApiError, isApiSuccess, unwrapApiData } from '@/services/apiEnvelope'
import config from '@/services/config'

const getQuoteAggregatedData = async (searchActionId, partNumbers, substitutes, customer) => {
    const response = await apiClient.post(QUOTE_AGGREGATED_DATA, {
        search_action_id: searchActionId,
        part_numbers: partNumbers,
        substitutes,
        customer
    })
    return unwrapApiData(response, null)
}

const searchQuotes = async (filters) => {
    const url = config.backendURL + QUOTE_SEARCH
    const range = filters?.range || []
    const today = moment.utc()

    const start = range[0] ? moment.utc(range[0]).format('YYYY-MM-DD') : today.format('YYYY-MM-DD')
    const end = range[1]
        ? moment.utc(range[1]).add(1, 'day').format('YYYY-MM-DD')
        : today.clone().add(1, 'day').format('YYYY-MM-DD')

    const isSearchDemo = config.isSearchDemo?.() ?? false
    const all_quotes = isSearchDemo ? false : !filters?.my_quotes
    const research = isSearchDemo ? true : !filters?.standard_quotes
    const unfinished_quotes = !filters?.all_quotes
    const isRetail = config.getSubdomain() === 'retail'

    const payload = {
        part_number: filters?.part,
        customer: isSearchDemo ? undefined : filters?.customer,
        customer_ref: isSearchDemo ? undefined : filters?.customer_ref,
        search_action_id: filters?.sa_id,
        date_start: start,
        date_end: end,
        all_quotes,
        unfinished: unfinished_quotes,
        research,
        limit: filters?.limit || filters?.pageSize || 20,
        offset: filters?.offset || 0,
    }

    if (isRetail) {
        payload.unassigned = Boolean(filters?.unassigned)
    }

    try {
        const res = await apiClient.get(url, { params: payload })
        const data = unwrapApiData(res, {})

        if (Array.isArray(data?.items) && typeof data?.total_count === 'number') {
            return { items: data.items, total_count: data.total_count }
        }

        if (Array.isArray(data?.search_actions)) {
            return { items: data.search_actions, total_count: data.search_actions.length }
        }

        if (!isApiSuccess(res)) {
            console.error(getApiError(res, 'Quote search failed'))
        }
    } catch (e) {
        console.error(JSON.stringify(e.message))
    }

    return { items: [], total_count: 0 }
}


const updatePricingActionReason = async (quoteId, userReason) => {
    const response = await apiClient.post(QUOTE_UPDATE_REASON, {
        quote_id: quoteId,
        user_reason: userReason,
    })

    return unwrapApiData(response, null)
}

const submitQuote = async (payload) => {
    const url = config.backendURL + QUOTE_CREATE

    // Align payload keys with backend schema.
    const normalizedPayload = {
        ...payload,
        search_action_id: payload.search_action_id,
        sales_order_id: payload.sales_order_id ?? null,
        ext_quote: null,
    }

    // API expects lead_time as a string; coerce if provided.
    if (
        Object.prototype.hasOwnProperty.call(normalizedPayload, 'lead_time') &&
        normalizedPayload.lead_time !== null &&
        normalizedPayload.lead_time !== undefined
    ) {
        normalizedPayload.lead_time = String(normalizedPayload.lead_time)
    }

    // API expects margin as a string; coerce if provided.
    if (
        Object.prototype.hasOwnProperty.call(normalizedPayload, 'margin') &&
        normalizedPayload.margin !== null &&
        normalizedPayload.margin !== undefined
    ) {
        normalizedPayload.margin = String(normalizedPayload.margin)
    }

    try {
        const method = normalizedPayload.id ? 'patch' : 'post'
        const res = await apiClient[method](url, normalizedPayload)
        const responseData = unwrapApiData(res, null)
        const persistedQuote = responseData?.quote || responseData

        return {
            data: persistedQuote,
            pricing_action: responseData?.pricing_action || null,
            payload: persistedQuote || normalizedPayload,
        }
    } catch (error) {
        // Surface backend validation (e.g., BPA minimum) to callers.
        const detail = error?.response?.data?.detail
        let formattedDetail = null

        // Normalize common backend error shapes so UI alerts are readable.
        let fields = []

        if (Array.isArray(detail)) {
            formattedDetail = detail
                .map((item) => {
                    if (typeof item === 'string') return item
                    if (item?.msg && item?.loc) {
                        const fieldPath = item.loc.join(' > ')
                        const fieldName = item.loc[item.loc.length - 1]
                        fields.push(fieldName)
                        return `${item.msg} (field: ${fieldName}, path: ${fieldPath})`
                    }
                    if (item?.msg) return item.msg
                    try {
                        return JSON.stringify(item)
                    } catch (e) {
                        return String(item)
                    }
                })
                .join('; ')
        } else if (detail && typeof detail === 'object') {
            formattedDetail = detail.error || detail.detail || JSON.stringify(detail)
        } else if (typeof detail === 'string') {
            formattedDetail = detail
        }

        const message =
            error?.response?.data?.error ||
            formattedDetail ||
            error?.message ||
            'Failed to submit quote.'

        const enhancedError = new Error(message)
        enhancedError.status = error?.response?.status ?? null
        enhancedError.backendError = error?.response?.data?.error || formattedDetail || null
        if (fields.length > 0) {
            enhancedError.fields = [...new Set(fields.filter(Boolean))]
        }

        throw enhancedError
    }
}

const assignQuoteToMe = async (searchActionId) => {
    const url = config.backendURL + QUOTE_ASSIGN_REQUESTER

    try {
        const res = await apiClient.post(url, { search_action_id: searchActionId })
        const data = unwrapApiData(res, null)

        if (data) {
            return data
        }

        return res?.data?.success ? res.data : null
    } catch (error) {
        const body = error?.response?.data

        return {
            success: false,
            error: body?.code || body?.error || error?.message || 'assign_failed',
            message: body?.error || error?.message || 'Unable to assign quote.',
            status: error?.response?.status ?? null,
        }
    }
}

const getQuotesBySearchActionId = async (searchActionId) => {
    try {
        const response = await apiClient.get(
            `${QUOTE_GET}/${searchActionId}`
        )
        return unwrapApiData(response, [])
    } catch (error) {
        console.error('Error fetching submitted quote data:', error)
    }
}

export default {
    searchQuotes,
    submitQuote,
    updatePricingActionReason,
    assignQuoteToMe,
    getQuoteAggregatedData,
    getQuotesBySearchActionId
}
